import React, { useEffect, useCallback, useContext } from 'react';

import { CircularProgress, ThemeProvider } from '@material-ui/core';
import { createTheme } from '@material-ui/core/styles';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { BrowserRouter, Switch, Route, Redirect } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';

import {
  clearAlerts,
  getAdminFullPermissions,
  fetchLoggedAdminCategories,
  refreshToken,
  getGeneralPermissions,
  getAdmin,
} from 'actions';
import { NotAuthorized } from 'app/components/not-authorized';
import { PageNotFound } from 'app/components/page-not-found';
import { PrivateRoute } from 'app/components/shared/private-route';
import { MasterConfig } from 'app/configuration';
import { FinancialControl } from 'app/financial-control';
import { Login } from 'app/login';
import { Operations } from 'app/operations';
import { SuperDash } from 'app/super-dash';
import { LanguageContext } from 'context/intl.context';
import { history, useNetwork, useTypedSelector } from 'helpers';
import { theme as applicationTheme } from 'index';
import {
  getLoggedAdminData,
  canReadMasterConfig,
  canReadOperations,
  canReadFinancialControl,
  getIsSuperAdmin,
  canReadAdminManagement,
  canReadZoning,
  canReadFares,
  hasReadAccessToMasterConfigSubSystem,
  canViewFareMediaBatches as canViewFareMediaBatchesSelector,
} from 'selectors';

import 'react-toastify/dist/ReactToastify.css';
import { AdminManagement } from './admin-management';
import { DIRECTIONS } from './i18n-locale/locales-constants';
import { Zoning } from './fare-products';
import Error from './fare-products/zoningRoutes/pages/Error';
import { CustomerSupport } from './customer-support';
import { CardsManagement } from './cards-management';
import { Reports } from './reports';
import { Settlements } from './settlements';
import TicketingInquiry from './transactions/ticketing-inquiry/TicketingInquiry';
const App: React.FC = () => {
  const dispatch = useDispatch();
  const alert = useTypedSelector(state => state.alert);
  const isLoggedIn = useTypedSelector(state => state.authentication?.loggedIn);
  const { locale, direction } = useContext(LanguageContext);
  const intl = useIntl();
  const language = locale?.slice(0, 2);

  const theme = createTheme({
    ...applicationTheme,
    direction,
  });

  const isLoadingPermissions = useTypedSelector(
    state => state.permissions.isLoading
  );

  const canViewAdminManagement = useTypedSelector(canReadAdminManagement);
  const canViewOperations = useTypedSelector(canReadOperations);
  const canViewFinancialControl = useTypedSelector(canReadFinancialControl);
  const canViewZoning = useTypedSelector(canReadZoning);
  const currentUser = useTypedSelector(getLoggedAdminData);
  const isSuperAdmin = useTypedSelector(getIsSuperAdmin);
  const canViewFares = useTypedSelector(canReadFares); 
  const currentUserId = currentUser?.id;

  const canViewMasterConfig = useTypedSelector(state =>
    canReadMasterConfig(state)
  );

  const isOnline = useNetwork();

  const clearAlert = useCallback(() => {
    dispatch(clearAlerts());
  }, [dispatch]);

  useEffect(() => {
    if (alert) {
      toast[alert.type as 'error' | 'success'](alert.message, {
        onClose: clearAlert,
      });
    }
  }, [alert, clearAlert]);

  useEffect(() => {
    history.listen(clearAlert);

    if (isSuperAdmin) {
      dispatch(getGeneralPermissions());
    }
  }, [clearAlert, isSuperAdmin, dispatch]);

  useEffect(() => {
    if (isLoggedIn && currentUserId) {
      dispatch(getAdmin(currentUserId));

      Promise.resolve(dispatch(getAdminFullPermissions(currentUserId))).then(
        () => dispatch(fetchLoggedAdminCategories())
      );
    } else {
      const publicPaths = ['/login', '/transactions/ticketing/inquiry', '/i'];
      const isPublicPath = publicPaths.some(p => window.location.pathname.startsWith(p));
      if (!isPublicPath) {
        history.push('/login');
      }
    }
  }, [currentUserId, dispatch, isLoggedIn]);

  // each 59 minutes the refresh token will be called, because it is the expiry time for the token
  useEffect(() => {
    let interval;
    if (isLoggedIn && isOnline) {
      interval = setInterval(() => {
        dispatch(refreshToken());
      }, 15 * 60 * 1000);
    } else {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [dispatch, isLoadingPermissions, isLoggedIn, isOnline]);
  const canViewCustomerSupport = useTypedSelector(state =>
    hasReadAccessToMasterConfigSubSystem(state, 'CustomerSupport')
  );
  //make CardsManagement can view mechanesm
  let canViewMerchants = useTypedSelector(state =>
    hasReadAccessToMasterConfigSubSystem(state, 'Merchants')
  );
  let canViewMerchantPOS = useTypedSelector(state =>
    hasReadAccessToMasterConfigSubSystem(state, 'MerchantPOS')
  );
  let canViewChargingTransactions = useTypedSelector(state =>
    hasReadAccessToMasterConfigSubSystem(state, 'ChargingTransactions')
  );
  let canViewFareMedia = useTypedSelector(state =>
    hasReadAccessToMasterConfigSubSystem(state, 'FareMedia')
  );
  let canViewTransferBalance = useTypedSelector(state =>
    hasReadAccessToMasterConfigSubSystem(state, 'TransferBalance')
  );
  const canViewFareMediaBatches = useTypedSelector(state =>
    canViewFareMediaBatchesSelector(state)
  );
  const canViewCardsManagement = canViewMerchants || canViewMerchantPOS || canViewChargingTransactions || canViewFareMedia || canViewTransferBalance || canViewFareMediaBatches ? true : false

  let canViewAFCReports = useTypedSelector(state =>
    hasReadAccessToMasterConfigSubSystem(state, 'AFCReports')
  );
  let canViewOperationsReports = useTypedSelector(state =>
    hasReadAccessToMasterConfigSubSystem(state, 'OperationsReports')
  );
  let canViewCardsReports = useTypedSelector(state =>
    hasReadAccessToMasterConfigSubSystem(state, 'CardsReports')
  );
  const canViewReports = canViewAFCReports || canViewOperationsReports || canViewCardsReports ? true : false
let canBanks = useTypedSelector(state =>
    hasReadAccessToMasterConfigSubSystem(state, 'Banks')
  );
   let canOrganisationBanks = useTypedSelector(state =>
    hasReadAccessToMasterConfigSubSystem(state, 'OrganisationBanks')
  );
   let canFinancialAccounts = useTypedSelector(state =>
    hasReadAccessToMasterConfigSubSystem(state, 'FinancialAccounts')
  );
   let canSettlementTransactions = useTypedSelector(state =>
    hasReadAccessToMasterConfigSubSystem(state, 'SettlementTransactions')
  );
  const canViewSettlements = canBanks || canOrganisationBanks || canFinancialAccounts || canSettlementTransactions ? true : false

  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        <ToastContainer
          position={direction === DIRECTIONS.RTL ? 'top-left' : 'top-right'}
          newestOnTop
          rtl={direction === DIRECTIONS.RTL}
        />

        <Helmet
          htmlAttributes={{
            lang: language,
          }}
          bodyAttributes={{
            dir: direction,
          }}
        >
          <title>{intl.formatMessage({ id: 'mwaslatMasrSuperDASH' })}</title>
        </Helmet>

        {isLoggedIn && isLoadingPermissions ? (
          <div
            style={{
              display: 'flex',
              height: '100vh',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <Switch>
            <Route path="/login">
              <Login />
            </Route>
            <Route path="/transactions/ticketing/inquiry">
              <TicketingInquiry />
            </Route>
            <Route path="/i">
              <TicketingInquiry />
            </Route>
            <Route path={`/Error`}>
          <Error />
        </Route>
            <Redirect from="/" exact to="/super-dash" />

            <PrivateRoute path="/super-dash">
              <SuperDash />
            </PrivateRoute>

            <PrivateRoute path="/customerSupport">
              {canViewCustomerSupport ? <CustomerSupport /> : <NotAuthorized />}
            </PrivateRoute>
            <PrivateRoute path="/CardsManagement">
              {canViewCardsManagement ? <CardsManagement /> : <NotAuthorized />}
            </PrivateRoute>
            <PrivateRoute path="/configuration">
              {canViewMasterConfig ? <MasterConfig /> : <NotAuthorized />}
            </PrivateRoute>

            <PrivateRoute path="/adminManagement">
              {canViewAdminManagement ? <AdminManagement /> : <NotAuthorized />}
            </PrivateRoute>

            <PrivateRoute path="/operations">
              {canViewOperations ? <Operations /> : <NotAuthorized />}
            </PrivateRoute>

            <PrivateRoute path="/financialControl">
              {canViewFinancialControl ? (
                <FinancialControl />
              ) : (
                <NotAuthorized />
              )}
            </PrivateRoute>

            <PrivateRoute path="/settlements">
              {canViewSettlements ? <Settlements /> : <NotAuthorized />}
            </PrivateRoute>

            <PrivateRoute path="/reports">
              {canViewReports ? <Reports /> : <NotAuthorized />}
            </PrivateRoute>

            <PrivateRoute path="/fare-products">
              {canViewFares ? (
                <Zoning />
              ) : (
                <NotAuthorized />
              )}
            </PrivateRoute>

            <PrivateRoute path="/">
              <PageNotFound />
            </PrivateRoute>
          </Switch>
        )}
      </BrowserRouter>
    </ThemeProvider>
  );
};

export { App };
